import { type Dispatch, type MutableRefObject, type SetStateAction, useCallback } from 'react'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { ShapeCommittedUndoMeta } from '@/lib/shapes/types/shape-committed-undo-meta'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { refitPenPathShapeBounds } from '@/lib/elements/pen-path-utils'
import { isShapeFrozenForGeometry } from '@/lib/groups/element-edit-policy'
import { pickPathGeometry } from '../helpers/path-geometry'
import type { CenterCanvasConfig } from '../types/center-canvas-config'

/**
 * The node edit overlay's and toolbar's path edits: a live path change on every drag tick, the
 * commit (with its undo entry) on release, and Reset back to the path the mode was entered with.
 */
export function useNodeEditPathHandlers({
  internalShapeManagerRef,
  nodeEditShapeData,
  setNodeEditShapeData,
  nodeEditPreDragShapeRef,
  nodeEditOriginalShapeRef,
  isNodeEditShapeGeometryBlocked,
  pulseSelectionGeometryBlocked,
  onShapeCommitted,
}: {
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  nodeEditShapeData: ShapeData | null
  setNodeEditShapeData: Dispatch<SetStateAction<ShapeData | null>>
  nodeEditPreDragShapeRef: MutableRefObject<ShapeData | null>
  nodeEditOriginalShapeRef: MutableRefObject<ShapeData | null>
  isNodeEditShapeGeometryBlocked: (shape: ShapeData) => boolean
  pulseSelectionGeometryBlocked: () => void
  onShapeCommitted: CenterCanvasConfig['onShapeCommitted']
}) {
  const handleNodeEditPathChange = useCallback(
    (
      shapeId: string,
      newPath: string,
      geom?: { x: number; y: number; width: number; height: number }
    ) => {
      const mgr = internalShapeManagerRef.current
      const existing = mgr?.getShapes().find((shape) => shape.id === shapeId)

      if (!existing || isNodeEditShapeGeometryBlocked(existing)) {
        if (existing) {
          const shapesById = new Map((mgr?.getShapes() ?? []).map((shape) => [shape.id, shape]))

          if (isShapeFrozenForGeometry(existing, shapesById)) {
            pulseSelectionGeometryBlocked()
          }
        }

        return
      }

      // Capture the shape state before any live updates this drag cycle so
      // handleNodeEditPathCommit can build a correct undo "before" snapshot.
      if (!nodeEditPreDragShapeRef.current) {
        nodeEditPreDragShapeRef.current = existing
      }

      const update = geom ? { path: newPath, ...geom } : { path: newPath }

      mgr?.updateShape(shapeId, update)
      setNodeEditShapeData((prev) => (prev ? { ...prev, path: newPath, ...(geom ?? {}) } : prev))
    },
    [
      isNodeEditShapeGeometryBlocked,
      pulseSelectionGeometryBlocked,
      internalShapeManagerRef,
      nodeEditPreDragShapeRef,
      setNodeEditShapeData,
    ]
  )

  const handleNodeEditPathCommit = useCallback(
    (shapeId: string, newPath: string) => {
      const mgr = internalShapeManagerRef.current

      if (!mgr) return
      const existing = mgr.getShapes().find((s) => s.id === shapeId)

      if (!existing?.path || isNodeEditShapeGeometryBlocked(existing)) {
        if (existing) {
          const shapesById = new Map(mgr.getShapes().map((shape) => [shape.id, shape]))

          if (isShapeFrozenForGeometry(existing, shapesById)) {
            pulseSelectionGeometryBlocked()
          }
        }

        nodeEditPreDragShapeRef.current = null
        return
      }

      // Use the shape state captured before the first live update this cycle as the
      // undo "before" snapshot. Falls back to `existing` when no live updates occurred.
      const preDrag = nodeEditPreDragShapeRef.current ?? existing

      nodeEditPreDragShapeRef.current = null

      const refitted = refitPenPathShapeBounds({ ...existing, path: newPath })

      mgr.updateShape(shapeId, pickPathGeometry(refitted))
      setNodeEditShapeData((prev) =>
        prev && prev.id === shapeId ? { ...prev, ...refitted } : prev
      )

      const undoMeta: ShapeCommittedUndoMeta = {
        before: pickPathGeometry(preDrag),
        after: pickPathGeometry(refitted),
      }

      onShapeCommitted?.(refitted, 'update', undoMeta)
    },
    [
      isNodeEditShapeGeometryBlocked,
      onShapeCommitted,
      pulseSelectionGeometryBlocked,
      internalShapeManagerRef,
      nodeEditPreDragShapeRef,
      setNodeEditShapeData,
    ]
  )

  const handleNodeEditReset = useCallback(() => {
    const original = nodeEditOriginalShapeRef.current

    if (!original || !nodeEditShapeData) return

    const mgr = internalShapeManagerRef.current

    if (!mgr) return

    const existing = mgr.getShapes().find((s) => s.id === original.id)

    if (!existing) return

    // Restore the exact position and path from when node-edit mode was entered.
    // Do NOT call refitPenPathShapeBounds — the original shape already has correct
    // bounds, and refitting from the current (edited) base would shift the position.
    const restoredShape = {
      ...existing,
      x: original.x,
      y: original.y,
      width: original.width,
      height: original.height,
      path: original.path ?? '',
      pathCommands: original.pathCommands,
    }

    mgr.updateShape(original.id, pickPathGeometry(restoredShape))

    nodeEditPreDragShapeRef.current = null

    const undoMeta: ShapeCommittedUndoMeta = {
      before: pickPathGeometry(existing),
      after: pickPathGeometry(restoredShape),
    }

    onShapeCommitted?.(restoredShape, 'update', undoMeta)
    // Stay in node edit mode so the user can continue editing after a reset.
    setNodeEditShapeData(restoredShape)
  }, [
    nodeEditShapeData,
    onShapeCommitted,
    internalShapeManagerRef,
    nodeEditOriginalShapeRef,
    nodeEditPreDragShapeRef,
    setNodeEditShapeData,
  ])

  return { handleNodeEditPathChange, handleNodeEditPathCommit, handleNodeEditReset }
}
